/* eslint-disable quotes */
/* eslint-disable indent */
import { createContext, useContext, ReactNode, useCallback } from "react";
import useSWRInfinite from "swr/infinite";
import { getBlogsKey } from "../../utils/keys";

export type Pagination = {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
};

export type BlogDescription = {
  title: string;
  description: string[];
};

export type BlogData = {
  id: number;
  documentId: string;
  heading: string;
  blogDescription: BlogDescription[];
  createdAt: string;
  slug: string;
  tags: string[];
  updatedAt: string;
  publishedAt: string;
  Image: { url: string }
};

export type BlogResponse = {
  data: BlogData[];
  meta?: {
    pagination?: Pagination;
  };
};

const BlogContext = createContext<{
  blogs: BlogData[];
  isLoading: boolean;
  error?: Error;
  totalPages: number;
  hasMore: boolean;
  loadMore: () => void;
  size: number;
  setSize: (size: number) => void;
}>({
  blogs: [],
  isLoading: false,
  error: undefined,
  totalPages: 0,
  hasMore: false,
  loadMore: () => { },
  size: 0,
  setSize: () => { }
});

export const useFetchBlog = () => {
  const fetcher = async (url: string) => {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      return await res.json();
    } catch (error) {
      console.error("Error fetching blogs:", error);
      throw error;
    }
  };

  const { data, error, isLoading, size, setSize } = useSWRInfinite<BlogResponse>(
    getBlogsKey,
    fetcher
  );

  const totalPages = data?.[0]?.meta?.pagination?.pageCount ?? 0;
  const currentPage = data?.[data.length - 1]?.meta?.pagination?.page ?? 1;
  const hasMore = currentPage < totalPages;

  const loadMore = useCallback(() => {
    if (hasMore) setSize((prev) => prev + 1);
  }, [hasMore, setSize]);

  const blogs = data?.flatMap((d) => d?.data) ?? [];

  return {
    blogs,
    error,
    isLoading,
    size,
    setSize,
    loadMore,
    totalPages,
    hasMore
  };
};


export const strapiURL = 'https://special-cherry-e44c650f77.strapiapp.com';


export const BlogProvider = ({ children }: { children: ReactNode }) => {
  const { blogs, isLoading, error, totalPages, hasMore, loadMore, size, setSize } = useFetchBlog();

  return (
    <BlogContext.Provider value={{ blogs, isLoading, error, totalPages, hasMore, loadMore, size, setSize }}>
      {children}
    </BlogContext.Provider>
  );
};

export const useBlogs = () => {
  const context = useContext(BlogContext);
  if (!context) {
    throw new Error("useBlogs must be used within a BlogProvider");
  }
  return context;
};
